/**
 * The hub while its data loads (Paper "Home · Loading"). Same frame as the
 * loaded page so nothing jumps: the real top bar, a headline skeleton at the
 * headline's size, the Plan + MCP well, and the Sites card with skeleton rows.
 * Labels that never change ("Current plan", "Sites", column names) render as
 * text; only values are bars.
 */

import type { ReactNode } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { PlusSignIcon } from "@hugeicons/core-free-icons"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { McpCard, PlanCard } from "./StatusCards"
import { SitesGridSkeleton } from "./SitesGrid"

/** 52px top bar: page title, then the view's actions on the right. */
export function HubTopBar({ children }: { children?: ReactNode }) {
  return (
    <div className="sticky top-0 z-10 flex h-[52px] shrink-0 items-center gap-2 border-b border-border/65 bg-card px-5">
      <h1 className="text-[13px] font-medium leading-4 text-foreground">Home</h1>
      <div className="flex-1" />
      {children}
    </div>
  )
}

export function AddSiteButton({
  onClick,
  disabled,
  quiet,
}: {
  onClick: () => void
  disabled?: boolean
  /** Outline while the account is dark: installing is the only primary action then. */
  quiet?: boolean
}) {
  return (
    <Button
      onClick={onClick}
      variant={quiet ? "outline" : "default"}
      className="h-[34px] gap-1.5 rounded-lg px-3 text-xs font-medium"
      disabled={disabled}
    >
      <HugeiconsIcon icon={PlusSignIcon} size={12} strokeWidth={2.4} />
      Add site
    </Button>
  )
}

/** Answer-first headline: one sentence (22/28), then the context line (13/16). */
export function HubHeadline({ headline, subline }: { headline: string; subline?: string | null }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground tabular-nums">
        {headline}
      </p>
      {subline && <p className="text-[13px] leading-4 text-muted-foreground">{subline}</p>}
    </div>
  )
}

const noop = () => {}

export default function HubLoading({
  basePath,
  onAddSite,
}: {
  /** Where "Set up MCP" points, same as the loaded card. */
  basePath: string
  onAddSite: () => void
}) {
  return (
    <div className="flex min-h-full flex-col" aria-busy="true">
      <HubTopBar>
        <AddSiteButton onClick={onAddSite} />
      </HubTopBar>
      <div className="flex flex-col gap-6 px-4 pb-9 pt-7 sm:px-9">
        <div className="flex flex-col gap-1.5" aria-hidden>
          <div className="flex h-7 items-center">
            <Skeleton className="h-7 w-[60%]" />
          </div>
          <div className="flex h-4 items-center">
            <Skeleton className="h-[13px] w-[38%]" />
          </div>
        </div>
        <div className="well grid grid-cols-1 gap-2 md:grid-cols-2">
          <PlanCard
            planName=""
            planTier=""
            isFreePlan={false}
            hasActiveTrial={false}
            hasActiveSubscription={false}
            usedTrial={false}
            onUpgrade={noop}
            onStartTrial={noop}
            onManageBilling={noop}
            isLoading
          />
          <McpCard basePath={basePath} isLoading />
        </div>
        <div className="well">
          <SitesGridSkeleton />
        </div>
      </div>
    </div>
  )
}
